import React, { useState } from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Button,
  Table,
  Badge,
  ActionIcon,
  TextInput,
  Select,
  Menu,
  Avatar,
  Stack,
  Card,
  SimpleGrid,
  rem,
  Modal,
  PasswordInput,
  Switch,
  Checkbox,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconPlus,
  IconEdit,
  IconTrash,
  IconDots,
  IconUser,
  IconMail,
  IconShield,
  IconCheck,
  IconKey,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useUsers } from '../../hooks/useUsers';
import { SmartLoader } from '../../components/common/LoadingStates';

interface UserFormData {
  email: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  school_id?: string;
  active: boolean;
  send_welcome_email: boolean;
}

const UsersPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'teacher',
    active: true,
    send_welcome_email: true,
  });

  const { data: usersData, isLoading, error } = useUsers({
    search: searchQuery,
    role: roleFilter || undefined,
    active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
  });

  const users = usersData?.users || [];

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'red';
      case 'admin': return 'orange';
      case 'teacher': return 'blue';
      case 'parent': return 'green';
      default: return 'gray';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
      case 'admin':
        return <IconShield size={14} />;
      case 'teacher':
        return <IconUser size={14} />;
      case 'parent':
        return <IconUser size={14} />;
      default:
        return <IconUser size={14} />;
    }
  };

  const handleCreateUser = async () => {
    if (formData.password !== formData.confirmPassword) {
      notifications.show({
        title: 'Error',
        message: 'Passwords do not match',
        color: 'red',
      });
      return;
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    notifications.show({
      title: 'User Created',
      message: 'User has been created successfully',
      color: 'green',
      icon: <IconCheck size={18} />,
    });
    
    closeCreateModal();
    resetForm();
  };

  const handleEditUser = (user: any) => {
    setFormData({
      email: user.email,
      password: '',
      confirmPassword: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      role: user.role,
      school_id: user.school_id,
      active: user.active,
      send_welcome_email: false,
    });
    openEditModal();
  };

  const handleUpdateUser = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    notifications.show({
      title: 'User Updated',
      message: 'User has been updated successfully',
      color: 'green',
      icon: <IconCheck size={18} />,
    });
    
    closeEditModal();
    resetForm();
  };

  const handleDeleteUser = async (_userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      notifications.show({
        title: 'User Deleted',
        message: 'User has been deleted successfully',
        color: 'green',
        icon: <IconCheck size={18} />,
      });
    }
  };

  const handleToggleUserStatus = async (_userId: string, active: boolean) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    notifications.show({
      title: active ? 'User Activated' : 'User Deactivated',
      message: `User has been ${active ? 'activated' : 'deactivated'} successfully`,
      color: 'green',
      icon: <IconCheck size={18} />,
    });
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'teacher',
      active: true,
      send_welcome_email: true,
    });
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u: any) => u.id));
    }
  };

  const stats = {
    total: users.length,
    active: users.filter((u: any) => u.active).length,
    inactive: users.filter((u: any) => !u.active).length,
    admins: users.filter((u: any) => u.role === 'admin' || u.role === 'super_admin').length,
    teachers: users.filter((u: any) => u.role === 'teacher').length,
    viewers: users.filter((u: any) => u.role === 'viewer').length,
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={2}>User Management</Title>
          <Text size="sm" c="dimmed">
            Manage system users and permissions
          </Text>
        </div>
        <Group>
          <Button
            leftSection={<IconUpload size={16} />}
            variant="light"
          >
            Import Users
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            variant="light"
          >
            Export
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={openCreateModal}
          >
            Add User
          </Button>
        </Group>
      </Group>

      {/* Statistics */}
      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }} spacing="md">
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Total</Text>
          <Text fw={700} size="xl">{stats.total}</Text>
        </Card>
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Active</Text>
          <Text fw={700} size="xl" c="green">{stats.active}</Text>
        </Card>
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Inactive</Text>
          <Text fw={700} size="xl" c="red">{stats.inactive}</Text>
        </Card>
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Admins</Text>
          <Text fw={700} size="xl" c="orange">{stats.admins}</Text>
        </Card>
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Teachers</Text>
          <Text fw={700} size="xl" c="blue">{stats.teachers}</Text>
        </Card>
        <Card withBorder p="xs">
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">Viewers</Text>
          <Text fw={700} size="xl" c="green">{stats.viewers}</Text>
        </Card>
      </SimpleGrid>

      {/* Filters */}
      <Paper withBorder p="md" radius="md">
        <Group>
          <TextInput
            placeholder="Search users..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by role"
            leftSection={<IconFilter size={16} />}
            data={[
              { value: 'all', label: 'All Roles' },
              { value: 'super_admin', label: 'Super Admin' },
              { value: 'admin', label: 'Admin' },
              { value: 'teacher', label: 'Teacher' },
              { value: 'parent', label: 'Parent' },
            ]}
            value={roleFilter}
            onChange={setRoleFilter}
            clearable
            w={180}
          />
          <Select
            placeholder="Filter by status"
            data={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
        </Group>
      </Paper>

      {/* Users Table */}
      <SmartLoader
        isLoading={isLoading}
        error={error as Error | null}
        isEmpty={!users.length}
        emptyState={
          <Card withBorder p="xl" ta="center">
            <Text size="lg" fw={500} mb="md">No users found</Text>
            <Text size="sm" c="dimmed" mb="xl">
              {searchQuery || roleFilter || statusFilter 
                ? "No users match your search criteria. Try adjusting your filters."
                : "Get started by adding your first user."}
            </Text>
            <Button onClick={openCreateModal} leftSection={<IconPlus size={16} />}>
              Add First User
            </Button>
          </Card>
        }
      >
        <Paper withBorder radius="md">
          {selectedUsers.length > 0 && (
            <Group p="md" pb={0}>
              <Text size="sm">{selectedUsers.length} users selected</Text>
              <Button size="xs" variant="light" color="red">
                Delete Selected
              </Button>
              <Button size="xs" variant="light">
                Export Selected
              </Button>
            </Group>
          )}
          
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={40}>
                  <Checkbox
                    checked={selectedUsers.length === users.length && users.length > 0}
                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                    onChange={handleSelectAll}
                  />
                </Table.Th>
                <Table.Th>User</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>School</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Last Login</Table.Th>
                <Table.Th w={100}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((user: any) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Checkbox
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.currentTarget.checked) {
                          setSelectedUsers([...selectedUsers, user.id]);
                        } else {
                          setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                        }
                      }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar size="sm" radius="xl" color={getRoleColor(user.role)}>
                        {(user.first_name || '')[0]}{(user.last_name || '')[0]}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>
                          {user.first_name || ''} {user.last_name || ''}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {user.email}
                        </Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={getRoleColor(user.role)}
                      leftSection={getRoleIcon(user.role)}
                      variant="light"
                    >
                      {user.role?.replace('_', ' ') || 'Unknown'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{user.school?.name || '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={user.active}
                      onChange={(e) => handleToggleUserStatus(user.id, e.currentTarget.checked)}
                      label={user.active ? 'Active' : 'Inactive'}
                      color={user.active ? 'green' : 'red'}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle">
                          <IconDots size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}
                          onClick={() => handleEditUser(user)}
                        >
                          Edit User
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconKey style={{ width: rem(14), height: rem(14) }} />}
                        >
                          Reset Password
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconMail style={{ width: rem(14), height: rem(14) }} />}
                        >
                          Send Welcome Email
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                          color="red"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          Delete User
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </SmartLoader>

      {/* Create User Modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title="Create New User"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="user@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Group grow>
            <PasswordInput
              label="Password"
              placeholder="Enter password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
            />
          </Group>
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="John"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </Group>
          <TextInput
            label="Phone"
            placeholder="+1 234 567 8900"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Select
            label="Role"
            placeholder="Select role"
            data={[
              { value: 'admin', label: 'Admin' },
              { value: 'teacher', label: 'Teacher' },
              { value: 'parent', label: 'Parent' },
            ]}
            value={formData.role}
            onChange={(value) => setFormData({ ...formData, role: value || 'teacher' })}
            required
          />
          <Switch
            label="Send welcome email"
            description="Send login credentials to the user"
            checked={formData.send_welcome_email}
            onChange={(e) => setFormData({ ...formData, send_welcome_email: e.currentTarget.checked })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button onClick={handleCreateUser}>
              Create User
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        opened={editModalOpened}
        onClose={closeEditModal}
        title="Edit User"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="user@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="John"
              value={formData.first_name}
              onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              required
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              value={formData.last_name}
              onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              required
            />
          </Group>
          <TextInput
            label="Phone"
            placeholder="+1 234 567 8900"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Select
            label="Role"
            placeholder="Select role"
            data={[
              { value: 'admin', label: 'Admin' },
              { value: 'teacher', label: 'Teacher' },
              { value: 'parent', label: 'Parent' },
            ]}
            value={formData.role}
            onChange={(value) => setFormData({ ...formData, role: value || 'teacher' })}
            required
          />
          <Switch
            label="Active"
            description="User can log in to the system"
            checked={formData.active}
            onChange={(e) => setFormData({ ...formData, active: e.currentTarget.checked })}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button onClick={handleUpdateUser}>
              Update User
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default UsersPage;